import React, { useState, useRef, useEffect } from 'react';
import { CircleCheck, CircleX, User } from 'lucide-react';
import { recentWeekKeys, timeAgo, weekLabel } from '../utils/mappingUsage.js';

const STRIP_WEEKS = 52;
// The list comes sorted by use: the first ones are those worth picking, more on demand
const PAGE_SIZE = 10;

const columnCount = (mapping) => mapping.sheets.reduce((acc, sheet) => acc + Object.keys(sheet.mapping || {}).length, 0);

function ValidityChip({ mapping }) {
  return mapping.valid ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
      <CircleCheck className="h-3 w-3" />
      {columnCount(mapping)} colonnes
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300"
      title="Ce mapping ne correspond pas aux feuilles ou aux colonnes de ce fichier"
    >
      <CircleX className="h-3 w-3" />
      Ne correspond pas
    </span>
  );
}

// One cell per week of the last year, filled when a load of that week used the mapping
function UsageStrip({ mapping }) {
  const used = new Set(mapping.used_week_keys || []);
  return (
    <div className="mt-2 flex items-center gap-3">
      <div className="flex gap-px" title={`Semaines chargées avec ce mapping, sur les ${STRIP_WEEKS} dernières`}>
        {recentWeekKeys(STRIP_WEEKS).map((key) => (
          <span
            key={key}
            title={`${weekLabel(key)}${used.has(key) ? ' : chargée' : ''}`}
            className={`h-4 w-1.5 rounded-sm ${used.has(key) ? 'bg-blue-500 dark:bg-blue-400' : 'bg-gray-200 dark:bg-gray-500/50'}`}
          />
        ))}
      </div>
      {mapping.used_weeks > 0 ? (
        <span
          className="text-sm text-gray-600 dark:text-gray-300"
          title={`${mapping.used_by_loads} chargement${mapping.used_by_loads > 1 ? 's' : ''}, le dernier le ${new Date(mapping.last_used).toLocaleDateString('fr-FR')}`}
        >
          <strong className="text-gray-900 dark:text-white">{mapping.used_weeks}</strong> sem. · {timeAgo(mapping.last_used)}
        </span>
      ) : (
        <span className="text-sm text-gray-400 dark:text-gray-400" title="Aucun chargement de l'Historique (hors chargements supprimés) n'utilise ce mapping">
          aucun chargement
        </span>
      )}
    </div>
  );
}

function SavedMappingsList({
  mappings,
  onMappingSelect,
  onDeleteMapping,
}) {
  // Use null to have all mappings collapsed by default.
  const [expandedMapping, setExpandedMapping] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const deleteInputRef = useRef(null);

  const toggleDetails = (filename) => {
    setExpandedMapping(expandedMapping === filename ? null : filename);
  };

  // Helper to generate a display name for a mapping based on its creation date.
  const getDisplayName = (mapping) => {
    const date = new Date(mapping.created);
    const formattedDate = date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    return `Sauvegarde du ${formattedDate}`;
  };

  const handleDeleteClick = (mapping) => {
    setMappingToDelete(mapping);
    setShowDeleteModal(true);
    setDeleteConfirmation('');
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmation === 'DELETE' && mappingToDelete) {
      onDeleteMapping(mappingToDelete.mapping_id);
      setShowDeleteModal(false);
      setMappingToDelete(null);
      setDeleteConfirmation('');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setMappingToDelete(null);
    setDeleteConfirmation('');
  };

  // Auto-focus sur l'input quand la modale s'ouvre
  useEffect(() => {
    if (showDeleteModal && deleteInputRef.current) {
      setTimeout(() => {
        deleteInputRef.current.focus();
      }, 100);
    }
  }, [showDeleteModal]);

  // Gérer l'appui sur Entrée
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && deleteConfirmation === 'DELETE') {
      handleDeleteConfirm();
    } else if (e.key === 'Escape') {
      handleDeleteCancel();
    }
  };

  return (
    <div>
      <div className="space-y-4">
        {mappings.slice(0, visibleCount).map((mapping) => (
          <div
            key={mapping.mapping_id}
            className="border p-4 rounded-lg shadow-sm bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-700"
          >
            <div className="flex justify-between items-center">
              <div
                className="cursor-pointer flex-1"
                onClick={() => toggleDetails(mapping.mapping_id)}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <h4
                    className="font-semibold text-lg text-gray-900 dark:text-gray-100"
                    title={`Créé le ${new Date(mapping.created).toLocaleString('fr-FR')} par ${mapping.owner}`}
                  >
                    {getDisplayName(mapping)}
                  </h4>
                  <ValidityChip mapping={mapping} />
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    <User className="h-3 w-3" />
                    {mapping.owner}
                  </span>
                </div>
                <UsageStrip mapping={mapping} />
                <div className="mt-2 flex items-center text-blue-600 dark:text-blue-400 text-sm">
                  {expandedMapping === mapping.mapping_id ? (
                    <span>&#9650; Masquer le détail</span>
                  ) : (
                    <span>&#9660; Voir le détail</span>
                  )}
                </div>
              </div>
              {mapping.valid && (
                <button
                  onClick={() => onMappingSelect(mapping)}
                  className="ml-4 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200"
                >
                  Utiliser ce mapping
                </button>
              )}
              {/* Only the creator can delete a mapping, and only while no load uses it: see delete-mapping */}
              {mapping.can_delete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (mapping.used_by_loads === 0) handleDeleteClick(mapping);
                  }}
                  disabled={mapping.used_by_loads > 0}
                  title={mapping.used_by_loads > 0
                    ? `Utilisé par ${mapping.used_by_loads} chargement${mapping.used_by_loads > 1 ? 's' : ''} de l'Historique : il ne peut pas être supprimé`
                    : 'Supprimer ce mapping'}
                  className="ml-4 px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded hover:bg-red-700 dark:hover:bg-red-600 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-red-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                </button>
              )}
            </div>
            {expandedMapping === mapping.mapping_id && (
              <div className="mt-4 border-t dark:border-gray-700 pt-2">
                {mapping.sheets.map((sheet, index) => (
                  <div key={index} className="mb-2">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100">
                      {sheet.sheet}
                    </h5>
                    <div className="text-sm">
                      {sheet.sheet_exists ? (
                        <span className="text-green-600 dark:text-green-400">
                          Feuille existante
                        </span>
                      ) : (
                        <span className="text-red-600 dark:text-red-400">
                          Feuille manquante
                        </span>
                      )}
                      {sheet.mapping && Object.keys(sheet.mapping).length > 0 && (
                        <table className="table-auto text-blue-600 dark:text-blue-400 mt-2">
                          <thead>
                            <tr>
                              <th className="px-2 py-1 border">File Column</th>
                              <th className="px-2 py-1 border">Mapped Column</th>
                              <th className="px-2 py-1 border">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(sheet.mapping).map(
                              ([fileColumn, mappedColumn]) => (
                                <tr key={fileColumn}>
                                  <td className="px-2 py-1 border">
                                    {fileColumn}
                                  </td>
                                  <td className="px-2 py-1 border">
                                    {mappedColumn}
                                  </td>
                                  <td className="px-2 py-1 border">
                                    {!sheet.sheet_exists || sheet.missing_columns.includes(fileColumn) ? '❌' : '✅'}
                                  </td>
                                </tr>
                              )
                            )}

                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {mappings.length > visibleCount && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Voir plus ({mappings.length - visibleCount} autre{mappings.length - visibleCount > 1 ? 's' : ''})
          </button>
        </div>
      )}

      {/* Modale de confirmation de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70"
            onClick={handleDeleteCancel}
          />

          {/* Modal */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-auto p-6 relative z-10">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 bg-red-100 dark:bg-red-900 p-2 rounded-full">
                <svg className="w-6 h-6 text-red-500 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Confirmer la suppression
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-300">
                    Êtes-vous sûr de vouloir supprimer le mapping "{mappingToDelete && getDisplayName(mappingToDelete)}" ?
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-300 mt-2">
                    Cette action est irréversible. Pour confirmer, tapez <span className="font-mono font-bold text-red-600 dark:text-red-400">DELETE</span> ci-dessous :
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <input
                ref={deleteInputRef}
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Tapez DELETE pour confirmer"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmation !== 'DELETE'}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  deleteConfirmation === 'DELETE'
                    ? 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600'
                    : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                }`}
              >
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SavedMappingsList;